import cn from "classnames"
import React from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import Icons from "@/images/signomatic/production_types"
import alu_name_badge from "@/images/signomatic/production_types/alu_name_badge.png"
import laser from "@/images/signomatic/production_types/laser.png"
import print from "@/images/signomatic/production_types/print.png"
import solid_plastic from "@/images/signomatic/production_types/solid_plastic.jpg"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

interface ProductionTypeDetailsProps {
  metaProduct: string
}

const ProductionTypeDetails: React.FC<ProductionTypeDetailsProps> = ({ metaProduct }) => {
  const { t } = useTranslation()

  return (
    <div className="described-details">
      <h4>
        {metaProduct === "meta_plastic_sign"
          ? `${t("production_type.laser")} / ${t("production_type.forex")} / ${t("production_type.solid_plastic")}`
          : `${t("production_type.laser")} / ${t("production_type.print")}`}
      </h4>
      <img src={laser} alt="" className="mb-4" />
      <h5>{t("production_type.laser")}</h5>
      <p className="paragraph-sm">{t("production_type.engraving_readmore")}</p>
      <img src={print} alt="" className="mb-4" />
      {metaProduct === "meta_plastic_sign" ? (
        <>
          <h5>{t("production_type.forex")}</h5>
          <p className="paragraph-sm">{t("production_type.forex_readmore")}</p>
        </>
      ) : (
        <>
          <h5>{t("production_type.print")}</h5>
          <p className="paragraph-sm">{t("production_type.print_readmore")}</p>
        </>
      )}
      <img src={solid_plastic} alt="" className="mb-4" />
      {metaProduct === "meta_plastic_sign" && (
        <>
          <h5>{t("production_type.solid_plastic")}</h5>
          <p className="paragraph-sm">{t("production_type.solid_plastic_readmore")}</p>
        </>
      )}
      {metaProduct === "meta_name_sign" && (
        <>
          <h5>{t("production_type.laser_etching")}</h5>
          <p className="paragraph-sm">{t("production_type.laser_etching_readmore")}</p>
          <img src={alu_name_badge} alt="" className="mb-4" />
        </>
      )}
    </div>
  )
}

interface ProductionTypeProps {
  type: string
  active: boolean
  adminOnly?: boolean
  update: () => void
  onReadMore: (e: React.MouseEvent) => void
  metaProduct: string
}

const ProductionType = ({ type, active, update, onReadMore, adminOnly, metaProduct }: ProductionTypeProps) => {
  const { t } = useTranslation()
  const translationType = metaProduct === "meta_plastic_sign" && type === "print" ? "forex" : type

  return (
    <div className={cn("described", `production_type-${type}`, { active })} onClick={update}>
      <div className="contents">
        <div className="image">{Icons[type] ? React.createElement(Icons[type]) : ""}</div>
        <div className="details">
          <h5 className="mb-0">
            {t(`production_type.${translationType}`)}
            {adminOnly && <span className="ml-2">(admin only)</span>}
          </h5>
          <p className="paragraph-2xs mb-0">{t(`production_type.${translationType}_description`)}</p>
          <div className="info-row">
            <p className="paragraph-xs mb-0">
              <a onClick={onReadMore}>{t("misc.readmore")} ...</a>
            </p>
            <SelectedIndicator active={active} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface ProductionTypeToolboxStateProps {
  signs: SignType[]
  data: SignData
  metaProduct: string
  subToolbox: string
}

interface ProductionTypeToolboxDispatchProps {
  setSubToolbox: typeof actions.setSubToolbox
}

type ProductionTypeToolboxProps = ProductionTypeToolboxStateProps &
  ProductionTypeToolboxDispatchProps &
  ToolboxStripItemProps

const ProductionTypeToolbox: React.FC<ProductionTypeToolboxProps> = ({
  sign,
  signs,
  data,
  metaProduct,
  subToolbox,
  setSubToolbox,
}) => {
  if (!data) {
    return <></>
  }

  const admin = !!(ClientData.admin && ClientData.admin.id)
  const signTypes = data.signtypes
  const currentSign = subToolbox ? signs.find(sign => `${sign.key}_production_type` === subToolbox) : null

  return (
    <Toolbox tKey="production_type">
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {signTypes?.map(signType => {
          const active = sign.signType === signType
          const signData = signs.find(item => item.key === signType)

          if (signData?.admin_only && !admin) {
            return
          }

          return (
            <ProductionType
              key={signType}
              type={signData?.production || ""}
              active={active}
              adminOnly={signData?.admin_only}
              metaProduct={metaProduct}
              update={() => {
                sign.setMetaSignType(metaProduct, signType)
              }}
              onReadMore={e => {
                e.stopPropagation()
                e.preventDefault()
                setSubToolbox(`${signType}_production_type`)
              }}
            />
          )
        })}
      </Scrollable>
      {subToolbox && currentSign && (
        <Scrollable>
          <ProductionTypeDetails metaProduct={metaProduct} />
        </Scrollable>
      )}
    </Toolbox>
  )
}

export default connect(
  ({ signs, currentSignData, metaProduct, subToolbox }: State) => ({
    signs,
    data: currentSignData,
    metaProduct,
    subToolbox,
  }),
  {
    setSubToolbox: actions.setSubToolbox,
  }
)(ProductionTypeToolbox)
